import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from '@barges/react';
import { getMyAccountBadgesApi } from '@common/api/api';
import classes from './classes.module.scss';
import { NTYPE } from '../../../common/config/const';
import { refreshReducersFromResponse } from '../../config/api_functions';
import { appRoutes } from '../../config/app_routes';
import { useLocalizedNavigate } from '../routes/hooks/useLocalizedNavigate';
import { useCurrentPath } from '../routes/hooks/useCurrentPath';
import { useNotification } from '../firebase_messaging_provider';

const renderNotificationTitle = (title) => (title ? (
  <div className={ classes.pushNotificationTitleContainer }>
    { title }
  </div>
) : null);

function PushNotification({
  loadHomePage,
  loadAdvisor,
  needToHandle,
  userbadgeCounters,
  advisors
}) {
  const history = useLocalizedNavigate();
  const currentPath = useCurrentPath();
  const { t } = useTranslation();
  const { notification, clearNotification } = useNotification();

  const [modalVisible, setModalVisible] = useState(false);

  const handlePsychicNotification = (onOpen) => {
    const { advisorId, advisorSeoName } = notification;
    if (onOpen) {
      clearNotification();
      history(appRoutes.advisor, { params: { slug: advisorSeoName } });
      history(0);
      return;
    }

    if (parseInt(currentPath?.params?.slug, 10) === advisorId) {
      loadAdvisor(advisorSeoName);
      clearNotification();
      return;
    }
    loadHomePage(true);
    setModalVisible(true);
  };

  const updateUserUnreadMessagesCountTotal = () => {
    const updatedUser = { badgeCounters: { ...userbadgeCounters, unreadMessagesCountTotal: userbadgeCounters.unreadMessagesCountTotal + 1 } };
    refreshReducersFromResponse({ user: updatedUser });
  };

  const updateUserActivityUnreadMessagesCount = () => {
    const { advisorId } = notification;
    const updatedAdvisor = {
      id: advisorId,
      liveModes: advisors[advisorId].liveModes,
      unreadMessagesCount: advisors[advisorId].unreadMessagesCount + 1
    };
    refreshReducersFromResponse({ advisor: updatedAdvisor });
  };

  const handleOrderMessageNotification = (onOpen) => {
    const { senderUserId, ntype, advisorId } = notification;
    getMyAccountBadgesApi({ advisorId }); // Need for update coupon label
    if (onOpen) {
      clearNotification();
      history(appRoutes.advisorOrders, { params: { id: senderUserId } });
      history(0);
      return;
    }

    if (currentPath?.params?.advisorId === senderUserId) {
      clearNotification();
      return;
    }

    updateUserActivityUnreadMessagesCount();
    updateUserUnreadMessagesCountTotal();

    if (currentPath.routeKey === appRoutes.myOrders) {
      clearNotification();
      return;
    }

    if (ntype !== NTYPE.ORDER_MESSAGE) {
      setModalVisible(true);
    }
  };

  const handleNotification = (onOpen = true) => {
    const { ntype } = notification;
    switch (ntype) {
      case NTYPE.PSYCHICS: {
        handlePsychicNotification(onOpen);
        break;
      }
      case NTYPE.ORDER_MESSAGE: {
        handleOrderMessageNotification(onOpen);
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    setModalVisible(false);

    if (notification && needToHandle) {
      handleNotification(false);
    }
  }, [notification]);

  if (!notification) return null;

  const renderContent = () => (
    <div className={ classes.pushNotificationContentContainer }>
      {renderNotificationTitle(notification.title)}
      <div className={ classes.pushNotificationTextContainer }>
        { notification.message || '' }
      </div>
      <div className={ classes.pushNotificationButtonsContainer }>
        <div>
          <button type="button" onClick={ clearNotification } className={ classes.pushNotificationCancelBtn }>
            { t('push.cancel') }
          </button>
        </div>
        <div>
          <button type="button" onClick={ handleNotification } className={ classes.pushNotificationOkBtn }>
            { t('push.open') }
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <Popover
      isOpen={ modalVisible && !!notification }
      centered
      className="push-notification-popover"
    >
      {renderContent()}
    </Popover>
  );
}

export default PushNotification;
